import React, { useEffect } from 'react';
import type { Anchor } from '@withjoy/joykit';
import { Box, useDisclosure } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { BurgerIconWrapper, PlainButtonWrapper } from './BurgerMenu.styles';
import type { MenuListProps } from './MenuList';
import { MenuList } from './MenuList';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import type { EventType } from '@graphql/generated-base';
import type { UniversalBurgerMenuEmbedHandlers } from '@apps/admin/components/UniversalBurgerMenu/UniversalBurgerMenu.types';

export type BurgerMenuRenderProps = Readonly<{
  isOpen: boolean;
}>;

export type BurgerMenuProps = Readonly<{
  enableMobileFirst?: boolean;
  items: MenuListProps['items'];
  DrawerHeaderContent?: React.ReactNode;
  DrawerFooterContent?: React.ReactNode;
  drawerAnchor?: Anchor;
  useModernMenu?: boolean;
  eventInfo?: {
    eventType: EventType;
    eventHandle: string;
    eventId: string;
  };
  /** Host hooks for iframe embeds. Only `onRegisterClose` is used here — @see UniversalBurgerMenu */
  embed?: UniversalBurgerMenuEmbedHandlers;
  /**
   * Children can be either ReactNode or a render prop function.
   * Use render prop to access menu state (e.g., isOpen for styling).
   */
  children?: React.ReactNode | ((props: BurgerMenuRenderProps) => React.ReactNode);
}>;

export const BurgerMenu: React.FC<BurgerMenuProps> = ({ items, DrawerFooterContent, DrawerHeaderContent, drawerAnchor, eventInfo, embed, children }) => {
  const { tq } = useTranslation('adminDashboardV3');
  const { isOpen, onToggle, onClose } = useDisclosure();
  const menuRef = useOuterClick(onToggle, isOpen);

  // This component owns the open state, so it's the only place that can hand an embed host a way
  // to close the drawer. Registration rather than a prop: the host closes imperatively, in response
  // to an event of its own, and a controlled `isOpen` would mean rewriting every in-app call site.
  useEffect(() => {
    embed?.onRegisterClose?.(onClose);
  }, [embed, onClose]);

  const isRenderProp = typeof children === 'function';
  const renderedChildren = isRenderProp ? children({ isOpen }) : children;

  return (
    <Box position="relative">
      {isRenderProp ? (
        <PlainButtonWrapper
          aria-label={tq`Menu`}
          onClick={() => onToggle()}
        >
          {renderedChildren}
        </PlainButtonWrapper>
      ) : (
        <BurgerIconWrapper
          aria-label={tq`Menu`}
          isActive={isOpen}
          onClick={() => onToggle()}
        >
          {renderedChildren}
        </BurgerIconWrapper>
      )}
      <MenuList
        menuRef={menuRef}
        isOpen={isOpen}
        onClose={onClose}
        items={items}
        DrawerFooterContent={DrawerFooterContent}
        DrawerHeaderContent={DrawerHeaderContent}
        drawerAnchor={drawerAnchor}
        eventInfo={eventInfo}
      />
    </Box>
  );
};
